import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { usePageContext } from "./use-page-context";
import type { PageContext } from "./use-page-context";

// ── types (reconciled against the landed apps/api/src/routes/assistant.ts) ──

export interface AssistantThread {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  /** First line of the latest message — the landed thread list includes it. */
  preview: string | null;
}

export interface ConfirmSpec {
  verb: string;
  object: string;
  consequence: string;
}

/** Includes "executing", the landed backend's transient confirm-claim state. */
export type ToolCallStatus =
  | "ok"
  | "error"
  | "pending_confirmation"
  | "executing"
  | "confirmed"
  | "dismissed"
  | "expired";

export interface ToolCallRecord {
  toolUseId: string;
  name: string;
  label: string;
  status: ToolCallStatus;
  summary?: string;
  confirm?: ConfirmSpec;
}

export interface AssistantMessage {
  id: string;
  role: "USER" | "ASSISTANT" | "TOOL";
  content: string;
  toolCalls: ToolCallRecord[] | null;
  createdAt: string;
}

export interface ToolStartedEvent {
  toolUseId: string;
  name: string;
  label: string;
}

export interface ToolResultEvent {
  toolUseId: string;
  name: string;
  ok: boolean;
  summary: string;
}

export interface ConfirmRequestEvent {
  confirmationId: string;
  messageId: string;
  tool: string;
  verb: string;
  object: string;
  consequence: string;
}

export interface DoneEvent {
  messageId: string;
  /** The landed backend forwards the model's stop reason. */
  stopReason?: string;
}

/** The landed backend's mid-stream failure event (partial turn persisted). */
export interface StreamErrorEvent {
  messageId?: string;
  message: string;
}

export interface StreamHandlers {
  onTextDelta?: (delta: string) => void;
  onToolStarted?: (ev: ToolStartedEvent) => void;
  onToolResult?: (ev: ToolResultEvent) => void;
  onConfirmRequest?: (ev: ConfirmRequestEvent) => void;
  onDone?: (ev: DoneEvent) => void;
  onStreamError?: (ev: StreamErrorEvent) => void;
}

export interface StreamMessageBody {
  content: string;
  pageContext?: PageContext;
}

export interface ConfirmationResult {
  status: "executed" | "dismissed";
  summary?: string;
}

/** Distinguishes user-stop/unmount cancellation from real failures. */
export function isAbortError(e: unknown): boolean {
  return (
    typeof e === "object" &&
    e !== null &&
    "name" in e &&
    (e as { name?: string }).name === "AbortError"
  );
}

// ── SSE frame parsing ──

function dispatchFrame(frame: string, handlers: StreamHandlers): void {
  let event = "";
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
    // anything else (comments `: hb`, blank lines) is ignored
  }
  if (!event || dataLines.length === 0) return;

  let payload: unknown;
  try {
    payload = JSON.parse(dataLines.join("\n"));
  } catch {
    return; // malformed frame — skip silently
  }

  switch (event) {
    case "text-delta":
      handlers.onTextDelta?.((payload as { delta: string }).delta);
      break;
    case "tool-started":
      handlers.onToolStarted?.(payload as ToolStartedEvent);
      break;
    case "tool-result":
      handlers.onToolResult?.(payload as ToolResultEvent);
      break;
    case "confirm-request":
      handlers.onConfirmRequest?.(payload as ConfirmRequestEvent);
      break;
    case "done":
      handlers.onDone?.(payload as DoneEvent);
      break;
    case "error":
      handlers.onStreamError?.(payload as StreamErrorEvent);
      break;
    default:
      break; // unknown event types are forward-compatible — ignore
  }
}

// ── API surface (testable without React) ──

export const assistantApi = {
  listThreads: (brandId: string) =>
    apiFetch<AssistantThread[]>(`/assistant/threads?brandId=${brandId}`),

  createThread: (brandId: string) =>
    apiFetch<AssistantThread>("/assistant/threads", {
      method: "POST",
      body: JSON.stringify({ brandId }),
    }),

  listMessages: (threadId: string) =>
    apiFetch<AssistantMessage[]>(`/assistant/threads/${threadId}/messages`),

  deleteThread: (threadId: string) =>
    apiFetch<{ id: string; deleted: boolean }>(`/assistant/threads/${threadId}`, {
      method: "DELETE",
    }),

  respondConfirmation: (
    messageId: string,
    body: { confirmationId: string; action: "confirm" | "dismiss" },
  ) =>
    apiFetch<ConfirmationResult>(`/assistant/confirmations/${messageId}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * SSE consumer. Raw fetch — apiFetch json()s everything and can't stream.
   * Frames are `event: <name>\ndata: <json>\n\n`; the buffer keeps trailing
   * partial frames across chunk boundaries; unknown events, comment
   * heartbeats and malformed data are skipped silently (forward compat).
   */
  async streamMessage(
    threadId: string,
    body: StreamMessageBody,
    handlers: StreamHandlers,
    signal?: AbortSignal,
  ): Promise<void> {
    const res = await fetch(`/api/assistant/threads/${threadId}/messages`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });

    if (!res.ok) {
      const json = await res.json().catch(() => ({
        error: { code: "UNKNOWN", message: "Request failed" },
      }));
      throw new ApiClientError(
        json.error?.code ?? "UNKNOWN",
        json.error?.message ?? "Request failed",
        res.status,
      );
    }
    if (!res.body) {
      throw new ApiClientError("NO_BODY", "Streaming response had no body", res.status);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        dispatchFrame(frame, handlers);
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) dispatchFrame(buffer, handlers);
  },
};

// ── query/mutation hooks ──

/**
 * Assistant tools mutate domain state (schedule a piece, approve, reply…)
 * while the page behind the panel keeps showing stale cache. Coarse,
 * brand-scoped invalidation: every query whose key contains the brandId
 * (calendar, pieces, activity, automation-config, …) refetches on next
 * observation. Assistant message keys are threadId-keyed, not brandId-keyed,
 * so they're untouched; ["assistant-threads", brandId] IS hit — desirable,
 * the turn may have retitled the thread.
 */
function invalidateBrandDomainQueries(qc: QueryClient, brandId: string): void {
  void qc.invalidateQueries({
    predicate: (q) => Array.isArray(q.queryKey) && q.queryKey.includes(brandId),
  });
}

export function useAssistantThreads(brandId: string | undefined) {
  return useQuery({
    queryKey: ["assistant-threads", brandId],
    queryFn: () => assistantApi.listThreads(brandId!),
    enabled: !!brandId,
  });
}

export function useThreadMessages(threadId: string | null) {
  return useQuery({
    queryKey: ["assistant-messages", threadId],
    queryFn: () => assistantApi.listMessages(threadId!),
    enabled: !!threadId,
  });
}

export function useCreateThread(brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => assistantApi.createThread(brandId!),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["assistant-threads", brandId] });
    },
    onError: () => {
      toast.error("Could not start a new conversation. Please try again.");
    },
  });
}

export function useDeleteThread(brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) => assistantApi.deleteThread(threadId),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["assistant-threads", brandId] });
    },
    onError: () => {
      toast.error("Could not delete the conversation. Please try again.");
    },
  });
}

export function useRespondConfirmation(
  threadId: string | null,
  brandId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      messageId: string;
      confirmationId: string;
      action: "confirm" | "dismiss";
    }) =>
      assistantApi.respondConfirmation(input.messageId, {
        confirmationId: input.confirmationId,
        action: input.action,
      }),
    onSuccess: (data) => {
      // A confirmed tool actually ran — refresh the page behind the panel.
      // Dismissals change nothing domain-side.
      if (data.status === "executed" && brandId) {
        invalidateBrandDomainQueries(qc, brandId);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["assistant-messages", threadId] });
    },
    onError: (e) => {
      if (e instanceof ApiClientError && e.status === 409) {
        toast.error("That action is no longer available — the item's state changed.");
      } else {
        toast.error("Could not complete the action. Please try again.");
      }
    },
  });
}

// ── streaming chat state ──

export interface LiveToolEvent {
  toolUseId: string;
  name: string;
  label: string;
  status: "running" | "ok" | "error";
  summary?: string;
}

export interface ChatError {
  code: string;
  message: string;
  status: number;
}

interface ChatState {
  optimisticUserMessage: string | null;
  streamText: string;
  toolEvents: LiveToolEvent[];
  pendingConfirms: ConfirmRequestEvent[];
  status: "idle" | "streaming" | "error";
  error: ChatError | null;
}

const INITIAL_CHAT_STATE: ChatState = {
  optimisticUserMessage: null,
  streamText: "",
  toolEvents: [],
  pendingConfirms: [],
  status: "idle",
  error: null,
};

type ChatAction =
  | { type: "start"; content: string }
  | { type: "text-delta"; delta: string }
  | { type: "tool-started"; ev: ToolStartedEvent }
  | { type: "tool-result"; ev: ToolResultEvent }
  | { type: "confirm-request"; ev: ConfirmRequestEvent }
  /** `clearLocal` drops the optimistic bubble + stream tail (the backend
   *  persisted the partial turn and the refetched history now shows it). */
  | { type: "error"; error: ChatError; clearLocal?: boolean }
  | { type: "reset" };

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "start":
      return {
        ...INITIAL_CHAT_STATE,
        optimisticUserMessage: action.content,
        status: "streaming",
      };
    case "text-delta":
      return { ...state, streamText: state.streamText + action.delta };
    case "tool-started":
      return {
        ...state,
        toolEvents: [
          ...state.toolEvents,
          { ...action.ev, status: "running" as const },
        ],
      };
    case "tool-result": {
      const found = state.toolEvents.some((t) => t.toolUseId === action.ev.toolUseId);
      const next: LiveToolEvent = {
        toolUseId: action.ev.toolUseId,
        name: action.ev.name,
        label: action.ev.name,
        status: action.ev.ok ? "ok" : "error",
        summary: action.ev.summary,
      };
      return {
        ...state,
        toolEvents: found
          ? state.toolEvents.map((t) =>
              t.toolUseId === action.ev.toolUseId
                ? { ...t, status: next.status, summary: next.summary }
                : t,
            )
          : [...state.toolEvents, next],
      };
    }
    case "confirm-request":
      return { ...state, pendingConfirms: [...state.pendingConfirms, action.ev] };
    case "error":
      return action.clearLocal
        ? { ...INITIAL_CHAT_STATE, status: "error", error: action.error }
        : { ...state, status: "error", error: action.error };
    case "reset":
      return INITIAL_CHAT_STATE;
    default:
      return state;
  }
}

/**
 * The stateful streaming hook behind the panel. Aborts in-flight streams on
 * unmount and on thread switch; on `done` it refetches the persisted
 * messages BEFORE clearing the local stream tail (no flash); on non-abort
 * failure it keeps the last content for `retry()` (spec §5: a dropped SSE
 * persists what completed — retry affordance, no rollback).
 */
export function useAssistantChat(brandId: string | undefined, threadId: string | null) {
  const qc = useQueryClient();
  const pageContext = usePageContext();
  const [state, dispatch] = useReducer(chatReducer, INITIAL_CHAT_STATE);
  const abortRef = useRef<AbortController | null>(null);
  /** Content + the thread it was sent to — retry must never cross-post. */
  const lastSentRef = useRef<{ content: string; threadId: string } | null>(null);
  /** The thread the in-flight stream targets (may differ from `threadId`
   *  for one render during lazy thread creation: null → new id). */
  const streamTargetRef = useRef<string | null>(null);
  const pageContextRef = useRef(pageContext);
  pageContextRef.current = pageContext;
  const statusRef = useRef(state.status);
  statusRef.current = state.status;
  const prevThreadIdRef = useRef(threadId);

  // Thread switch: abort any stream that does NOT target the new thread.
  // (Lazy creation sends against the new id before the store updates —
  // that stream must survive the null → id transition.) Even when no stream
  // is in flight (streamTargetRef already cleared after an error), a switch
  // must not carry error/optimistic state into the other thread.
  useEffect(() => {
    const threadChanged = prevThreadIdRef.current !== threadId;
    prevThreadIdRef.current = threadId;
    if (!threadChanged) return;
    // A live stream already targeting the new thread (lazy create) survives.
    if (streamTargetRef.current !== null && streamTargetRef.current === threadId) return;

    if (streamTargetRef.current) {
      abortRef.current?.abort();
      abortRef.current = null;
      streamTargetRef.current = null;
      dispatch({ type: "reset" });
    } else if (statusRef.current !== "idle") {
      dispatch({ type: "reset" });
    }
  }, [threadId]);

  // Unmount: always abort.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const send = useCallback(
    async (content: string, threadIdOverride?: string) => {
      const targetThreadId = threadIdOverride ?? threadId;
      if (!targetThreadId || !content.trim()) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      streamTargetRef.current = targetThreadId;
      lastSentRef.current = { content, threadId: targetThreadId };
      dispatch({ type: "start", content });

      let sawDone = false;
      // Any stream event arriving ⇒ the backend started (and persisted)
      // the turn; decides whether the local tail duplicates refetched rows.
      let sawStreamEvent = false;
      // Any tool ran this turn ⇒ domain state may have changed behind the
      // panel; a read-only (text-only) turn must NOT churn the page's cache.
      let hadToolActivity = false;
      let streamError: StreamErrorEvent | null = null;
      // Stale streams must never mutate state after an abort.
      const guarded =
        <T,>(fn: (ev: T) => void) =>
        (ev: T) => {
          if (controller.signal.aborted) return;
          sawStreamEvent = true;
          fn(ev);
        };
      try {
        await assistantApi.streamMessage(
          targetThreadId,
          { content, pageContext: pageContextRef.current },
          {
            onTextDelta: guarded((delta: string) => dispatch({ type: "text-delta", delta })),
            onToolStarted: guarded((ev: ToolStartedEvent) =>
              dispatch({ type: "tool-started", ev }),
            ),
            onToolResult: guarded((ev: ToolResultEvent) => {
              hadToolActivity = true;
              dispatch({ type: "tool-result", ev });
            }),
            onConfirmRequest: guarded((ev: ConfirmRequestEvent) =>
              dispatch({ type: "confirm-request", ev }),
            ),
            onDone: () => {
              sawDone = true;
            },
            onStreamError: guarded((ev: StreamErrorEvent) => {
              streamError = ev;
            }),
          },
          controller.signal,
        );

        if (controller.signal.aborted) return;

        // Refetch persisted rows BEFORE dropping the local tail (no flash).
        await Promise.all([
          qc.invalidateQueries({ queryKey: ["assistant-messages", targetThreadId] }),
          qc.invalidateQueries({ queryKey: ["assistant-threads", brandId] }),
        ]);
        if (controller.signal.aborted) return;

        if (sawDone) {
          dispatch({ type: "reset" });
          // Tools executed this turn — refresh the brand's domain queries so
          // the page behind the panel reflects what the assistant just did.
          if (hadToolActivity && brandId) {
            invalidateBrandDomainQueries(qc, brandId);
          }
        } else {
          // Mid-stream error event, or a stream that ended with neither
          // `done` nor `error` (truncated — surface the retry affordance).
          // When events arrived the backend persisted the partial turn and
          // the refetch above shows it — drop the duplicate local tail.
          dispatch({
            type: "error",
            error: {
              code: "STREAM_ERROR",
              message:
                (streamError as StreamErrorEvent | null)?.message ??
                "The response ended unexpectedly.",
              status: 0,
            },
            clearLocal: sawStreamEvent,
          });
        }
      } catch (e) {
        if (isAbortError(e) || controller.signal.aborted) return;
        const err =
          e instanceof ApiClientError
            ? { code: e.code, message: e.message, status: e.status }
            : { code: "UNKNOWN", message: "Something went wrong mid-response.", status: 0 };
        if (sawStreamEvent) {
          // Mid-stream drop — the backend persisted what completed. Refetch
          // first, then clear the duplicated local tail, keeping the error.
          await qc.invalidateQueries({ queryKey: ["assistant-messages", targetThreadId] });
          if (controller.signal.aborted) return;
          dispatch({ type: "error", error: err, clearLocal: true });
        } else {
          // Pre-stream failure (e.g. 402 budget refusal) — nothing was
          // persisted; keep the optimistic bubble so retry re-sends visibly.
          dispatch({ type: "error", error: err });
          void qc.invalidateQueries({ queryKey: ["assistant-messages", targetThreadId] });
        }
      } finally {
        if (abortRef.current === controller) {
          streamTargetRef.current = null;
        }
      }
    },
    [brandId, threadId, qc],
  );

  const retry = useCallback(() => {
    const last = lastSentRef.current;
    if (!last) return;
    if (threadId !== last.threadId) {
      // The active thread changed since the failure — never cross-post the
      // retry into another thread; drop the stale retry state instead.
      lastSentRef.current = null;
      dispatch({ type: "reset" });
      return;
    }
    void send(last.content, last.threadId);
  }, [send, threadId]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    streamTargetRef.current = null;
    dispatch({ type: "reset" });
  }, []);

  return { ...state, send, retry, stop };
}
